/**
 * Maps Firebase HTTPS callable (and common Cloud Functions) errors to user-safe messages.
 * Pillar 3/8: consistent UX; technical details stay in console / Sentry.
 *
 * Works with firebase/compat errors where `error.code` is set (e.g. functions/unauthenticated).
 */

/** @param {unknown} err */
function getErrorCode(err) {
  if (err && typeof err === "object" && "code" in err && typeof err.code === "string") {
    return err.code;
  }
  return null;
}

/**
 * @param {unknown} error - Firebase HttpsError or generic Error
 * @returns {{ userMessage: string, code: string | null }}
 */
export function getFirebaseHttpsCallableUserMessage(error) {
  const code = getErrorCode(error);
  const generic =
    "Something went wrong on our end. Please try again in a moment. If it keeps happening, contact support.";

  if (!code) {
    return { userMessage: generic, code: null };
  }

  const map = {
    "functions/unauthenticated":
      "Your session expired or you are not signed in. Please sign in and try again.",
    "functions/permission-denied": "You do not have permission to do this.",
    "functions/not-found": "This action is not available right now. Please try again later.",
    "functions/invalid-argument":
      "Some information was not accepted. Check your input and try again.",
    "functions/failed-precondition":
      "This action cannot be completed in the current state. Try again or refresh the page.",
    "functions/out-of-range":
      "The request was out of allowed limits. Adjust your input and try again.",
    "functions/unimplemented": "This feature is not available yet.",
    "functions/internal": generic,
    "functions/unavailable": "Our servers are busy. Please try again in a few minutes.",
    "functions/data-loss": generic,
    "functions/deadline-exceeded":
      "The request took too long and timed out. Try again with a shorter input or later.",
    "functions/cancelled": "The request was cancelled.",
    "functions/already-exists": "This item already exists.",
    "functions/resource-exhausted": "Too many requests. Please wait a moment and try again.",
    "functions/aborted": "The operation was interrupted. Please try again.",
  };

  const userMessage = map[code] || generic;
  return { userMessage, code };
}

/**
 * Wraps a callable error as a new Error with a safe .message for UI; preserves original on .cause when available.
 * @param {unknown} error
 * @param {{ feature?: string }} [context]
 */
export function wrapFirebaseCallableErrorForUser(error, context = {}) {
  const { userMessage, code } = getFirebaseHttpsCallableUserMessage(error);
  const wrapped = new Error(userMessage);
  wrapped.name = "FirebaseCallableUserError";
  if (code) {
    wrapped.code = code;
  }
  if (context.feature) {
    wrapped.feature = context.feature;
  }
  try {
    wrapped.cause = error;
  } catch (e) {
    console.warn("[firebaseHttpsCallableUserMessage] Could not set error.cause", e);
  }
  return wrapped;
}
